import { useToast as useToastNotification } from "../components/ToastNotification";

type ToastType = "success" | "error" | "warning" | "info";

/**
 * Hook for showing toast notifications
 * 
 * Provides a simple interface for displaying toast notifications
 * with automatic duration based on toast type.
 * 
 * @returns Object with toast methods
 * 
 * @example
 * const toast = useToast();
 * 
 * // Show success toast
 * toast.success("Business created successfully");
 * 
 * // Show error toast
 * toast.error("Failed to save changes");
 * 
 * // Show warning toast
 * toast.warning("This action cannot be undone");
 * 
 * // Show info toast
 * toast.info("New features available");
 * 
 * // Show toast with custom duration
 * toast.show("success", "Custom message", 3000);
 */
export function useToast() {
  const { showToast } = useToastNotification();

  return {
    /**
     * Shows a success toast (auto-dismisses after 5 seconds)
     */
    success: (message: string, duration?: number) => {
      void duration;
      showToast(message, "success");
    },

    /**
     * Shows an error toast (auto-dismisses after 7 seconds)
     */
    error: (message: string, duration?: number) => {
      void duration;
      showToast(message, "error");
    },

    /**
     * Shows a warning toast (auto-dismisses after 6 seconds)
     */
    warning: (message: string, duration?: number) => {
      void duration;
      showToast(message, "warning");
    },

    /**
     * Shows an info toast (auto-dismisses after 5 seconds)
     */
    info: (message: string, duration?: number) => {
      void duration;
      showToast(message, "info");
    },

    /**
     * Shows a toast with custom type and duration
     */
    show: (type: ToastType, message: string, duration?: number) => {
      void duration;
      showToast(message, type);
    },
  };
}
